import { expandImplies } from "@savvy/shared";
import { create } from "zustand";

import { ApiClient } from "../api/client";
import { identity } from "../api/services";
import type { TokenResponse } from "../api/types";
import { clearSession, loadSession, saveSession } from "./session";

export type AuthStatus = "anonymous" | "authenticating" | "authenticated" | "expired";

export interface AuthState {
  status: AuthStatus;
  username: string;
  /** Raw grants from the server. */
  granted: string[];
  /** Implies-expanded, which is what the UI gates on. */
  effective: Set<string>;
  error: string | null;

  api: ApiClient;

  login(username: string, password: string): Promise<void>;
  signOut(reason?: string): void;
  /** Re-reads capabilities from the server. */
  refreshPermissions(): Promise<void>;
  /** Restores a stored session, verifying it before trusting it. */
  restore(): Promise<void>;
}

/** Tokens live outside the store: they are not render state, and keeping them
 *  out means a careless component cannot put one on screen. */
let accessToken: string | null = null;
let refreshToken: string | null = null;
let expiresAt = 0;
let refreshTimer: ReturnType<typeof setTimeout> | null = null;

export const useAuth = create<AuthState>((set, get) => {
  // Annotated explicitly: `refresh` closes over `api`, so without this
  // TypeScript cannot infer the type of a value referenced inside its own
  // initializer.
  const api: ApiClient = new ApiClient({
    getToken: () => accessToken,
    refresh: async (): Promise<string | null> => {
      if (!refreshToken) return null;
      try {
        const t: TokenResponse = await identity(api).refresh(refreshToken);
        applyTokens(t.access_token, t.refresh_token, t.expires_in);
        return t.access_token;
      } catch {
        return null;
      }
    },
    onSignOut: (reason) => get().signOut(reason),
  });

  function applyTokens(access: string, refresh: string, expiresIn: number) {
    accessToken = access;
    refreshToken = refresh;
    expiresAt = Date.now() + expiresIn * 1000;

    // Refresh a minute early, so a wallboard left on a screen overnight does
    // not die at the token's expiry and show a frozen floor.
    if (refreshTimer) clearTimeout(refreshTimer);
    const delay = Math.max(5_000, expiresIn * 1000 - 60_000);
    refreshTimer = setTimeout(() => {
      void api.request("GET", "/api/identity/connect/permissions").catch(() => {});
    }, delay);

    persist();
  }

  function persist() {
    if (!accessToken || !refreshToken) return;
    saveSession({
      accessToken,
      refreshToken,
      expiresAt,
      username: get().username,
      caps: get().granted,
    });
  }

  function setCaps(granted: string[]) {
    set({ granted, effective: expandImplies(granted) });
  }

  return {
    status: "anonymous",
    username: "",
    granted: [],
    effective: new Set<string>(),
    error: null,
    api,

    async login(username, password) {
      set({ status: "authenticating", error: null });
      try {
        const t = await identity(api).login(username, password);
        set({ username: t.user?.username ?? username });
        applyTokens(t.access_token, t.refresh_token, t.expires_in);

        const perms = await identity(api).permissions();
        setCaps(perms.caps ?? []);
        set({ status: "authenticated" });
        persist();
      } catch (e) {
        accessToken = refreshToken = null;
        clearSession();
        set({ status: "anonymous", error: e instanceof Error ? e.message : String(e) });
      }
    },

    signOut(reason) {
      if (refreshTimer) clearTimeout(refreshTimer);
      refreshTimer = null;
      accessToken = refreshToken = null;
      expiresAt = 0;
      clearSession();
      set({
        status: reason ? "expired" : "anonymous",
        username: "",
        granted: [],
        effective: new Set<string>(),
        error: reason ?? null,
      });
    },

    async refreshPermissions() {
      const perms = await identity(api).permissions();
      setCaps(perms.caps ?? []);
      persist();
    },

    async restore() {
      const s = loadSession();
      if (!s) return;

      accessToken = s.accessToken;
      refreshToken = s.refreshToken;
      expiresAt = s.expiresAt;
      set({ username: s.username });

      // Verify before trusting. A stored token may be expired or revoked, and
      // re-reading capabilities means an admin's grant takes effect without the
      // user signing out and in again.
      try {
        const perms = await identity(api).permissions();
        setCaps(perms.caps ?? []);
        set({ status: "authenticated" });
        persist();
      } catch {
        get().signOut("stored session is no longer valid");
      }
    },
  };
});

/** The current access token, for callers that must pass it explicitly. */
export function currentAccessToken(): string | null {
  return accessToken;
}
